/**
 * @module ConstructorIO/search
 */

import RefinementURLBuilder from '../../spa/interface/refinement-url-builder';
import {decodeValue} from '../helpers/url';

/**
 * @implements {RefinementURLBuilder}
 */
export default class ConstructorRefinementURLBuilder extends RefinementURLBuilder {
    /**
     * @param {URLSearchParams} searchParams - `URLSearchParams` of the current URL
     * @param {string} id - id of the refinement
     * @param {any} value - value of the refinement
     */
    static #appendFilterToURL(searchParams, id, value) {
        const processedValue = decodeValue(value);
        const values = searchParams.get(`filters[${id}]`)?.split(',');
        if (values) {
            values.push(processedValue);
            searchParams.set(`filters[${id}]`, values.join(','));
        } else {
            searchParams.set(`filters[${id}]`, processedValue);
        }
    }

    /**
     *@returns {string} url
     */
    static getStartingURL() {
        const url = new URL(window.location.href);
        const refinementsToDelete = [];
        url.searchParams.forEach((value, key) => {
            if (key.includes('filter')) {
                refinementsToDelete.push(key);
            }
        });
        url.searchParams.delete('page');
        refinementsToDelete.forEach(refinement => url.searchParams.delete(refinement));
        return url.toString();
    }

    /**
     * @param {string} id - id of the refinement
     * @param {any} value - value of the refinement
     * @returns {string} url
     */
    static getRefinementValueURL(id, value) {
        const url = new URL(window.location.href);
        if (!id || !value) {
            return url.toString();
        }

        const {searchParams} = url;
        const processedValue = decodeValue(value);
        const values = searchParams.get(`filters[${id}]`)?.split(',');
        if (values) {
            values.push(processedValue);
            searchParams.set(`filters[${id}]`, values.join(','));
        } else {
            searchParams.set(`filters[${id}]`, processedValue);
        }
        return url.toString();
    }

    /**
     * @param {Object<string, any>} filters - filters to append
     * @param {string} refineURL - updated URL with required params.
     * @returns {string} url
     */
    static getMultipleRefinementsValuesURL(filters, refineURL) {
        const url = new URL(refineURL || window.location.href);
        if (!filters) {
            return url.toString();
        }
        const {searchParams} = url;
        for (const [id, value] of Object.entries(filters)) {
            this.#appendFilterToURL(searchParams, id, value);
        }
        return url.toString();
    }

    /**
     * @param {string} id - id of the refinement
     * @param {any} value - value of the refinement
     * @returns {string} url
     */
    static getRefinementValueRemoveURL(id, value) {
        const url = new URL(window.location.href);
        if (!id || !value) {
            return url.toString();
        }

        const {searchParams} = url;
        const processedValue = decodeValue(value);
        const values = searchParams
            .get(`filters[${id}]`)
            ?.split(',')
            .filter(v => v !== processedValue);
        if (!values || values.length === 0) {
            searchParams.delete(`filters[${id}]`);
        } else {
            searchParams.set(`filters[${id}]`, values.join(','));
        }
        return url.toString();
    }

    /**
     * @param {string} id - id of the refinement
     * @returns {string} url
     */
    static getRefinementRemoveURL(id) {
        const url = new URL(window.location.href);
        if (!id) {
            return url.toString();
        }
        url.searchParams.delete(`filters[${id}]`);
        return url.toString();
    }

    /**
     * @param {string} nextPage - next page
     * @returns {string} url
     */
    static getNextPageURL(nextPage) {
        const url = new URL(window.location.href);
        if (!nextPage) {
            return url.toString();
        }
        url.searchParams.set('page', nextPage);
        return url.toString();
    }
}
